import { ChevronRight, FileQuestion } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { usePortalPagina, usePortalSecoes, type PortalSecao } from '@/lib/usePortalContent';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PortalHero } from '@/ui/PortalHero';
import { Spinner } from '@/ui/Spinner';

import { cabecalhoBaixo, capaDaPagina } from './imagensDeCapa';
import {
  CONSULTAS_POR_PAGINA,
  PROXIMO_PASSO_CREDENCIAMENTO,
  type ItemPublico,
} from './navegacaoPublica';
import { ProximoPassoCredenciamento } from './ProximoPassoCredenciamento';

/**
 * As consultas públicas fixas da página (`CONSULTAS_POR_PAGINA`): telas do
 * sistema, não texto editorial. O cartão inteiro é o link.
 */
function ConsultasDaPagina({ consultas }: { consultas: readonly ItemPublico[] }) {
  return (
    <section aria-labelledby="consultas-publicas">
      <h2 id="consultas-publicas" className="text-xl font-bold">
        Consultas públicas
      </h2>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2">
        {consultas.map((consulta) => (
          <li key={consulta.caminho}>
            <Link
              to={consulta.caminho}
              className="flex h-full items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <span>
                <span className="block font-semibold text-foreground">{consulta.titulo}</span>
                {consulta.descricao ? (
                  <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
                    {consulta.descricao}
                  </span>
                ) : null}
              </span>
              <ChevronRight
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Página do portal público servida pelo conteúdo editorial, pelo slug.
 *
 * O conteúdo e o editor da Administração já existiam (`/admin/portal/paginas`);
 * o que faltava era esta rota — quem publicava uma página não tinha endereço
 * público onde ela aparecesse. Endereço canônico: `/portal/:slug`. As páginas
 * institucionais do menu (`/programa`, `/legislacao`, ...) chegam aqui pelo
 * mesmo componente, com o slug fixo (ver `navegacaoPublica.ts`).
 */

/** Lê um campo de texto do conteúdo da seção, que é JSON livre vindo do editor. */
function texto(de: Record<string, unknown>, campo: string): string | null {
  const valor = de[campo];
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

/** Lê a lista de itens da seção. Cada item é objeto ou texto solto. */
function itens(de: Record<string, unknown>): Record<string, unknown>[] {
  const valor = de['itens'];
  if (!Array.isArray(valor)) {
    return [];
  }
  return valor.map((item) =>
    typeof item === 'object' && item !== null
      ? (item as Record<string, unknown>)
      : { titulo: String(item) },
  );
}

function CorpoDaSecao({ secao }: { secao: PortalSecao }) {
  const conteudo = secao.conteudo ?? {};
  const lista = itens(conteudo);

  switch (secao.tipo) {
    case 'cards':
      return (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lista.map((item, indice) => (
            <li
              key={texto(item, 'titulo') ?? indice}
              className="rounded-lg border border-border bg-card p-4"
            >
              <p className="font-semibold">{texto(item, 'titulo')}</p>
              {texto(item, 'descricao') ? (
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {texto(item, 'descricao')}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      );

    case 'stats':
      return (
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {lista.map((item, indice) => (
            <div
              key={texto(item, 'rotulo') ?? indice}
              className="rounded-lg border border-border bg-card p-4"
            >
              <dt className="text-sm text-muted-foreground">{texto(item, 'rotulo')}</dt>
              <dd className="mt-1 text-2xl font-bold">{texto(item, 'valor')}</dd>
            </div>
          ))}
        </dl>
      );

    case 'timeline':
      return (
        <ol className="space-y-4 border-l border-border pl-6">
          {lista.map((item, indice) => (
            <li key={texto(item, 'titulo') ?? indice} className="relative">
              <span
                className="absolute left-[-1.9rem] top-1.5 h-3 w-3 rounded-full bg-primary"
                aria-hidden="true"
              />
              <p className="font-semibold">{texto(item, 'titulo')}</p>
              {texto(item, 'descricao') ? (
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {texto(item, 'descricao')}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      );

    case 'faq':
      return (
        <dl className="space-y-4">
          {lista.map((item, indice) => (
            <div key={texto(item, 'pergunta') ?? indice}>
              <dt className="font-semibold">{texto(item, 'pergunta') ?? texto(item, 'titulo')}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {texto(item, 'resposta') ?? texto(item, 'descricao')}
              </dd>
            </div>
          ))}
        </dl>
      );

    case 'lista':
      return (
        <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed">
          {lista.map((item, indice) => (
            <li key={texto(item, 'titulo') ?? indice}>
              {texto(item, 'titulo')}
              {texto(item, 'descricao') ? (
                <span className="text-muted-foreground"> — {texto(item, 'descricao')}</span>
              ) : null}
            </li>
          ))}
        </ul>
      );

    case 'citacao':
      return (
        <blockquote className="border-l-4 border-primary pl-4 italic">
          <p className="leading-relaxed">{texto(conteudo, 'texto')}</p>
          {texto(conteudo, 'autor') ? (
            <footer className="mt-2 text-sm not-italic text-muted-foreground">
              {texto(conteudo, 'autor')}
            </footer>
          ) : null}
        </blockquote>
      );

    default:
      return (
        <p className="max-w-3xl whitespace-pre-line leading-relaxed text-muted-foreground">
          {texto(conteudo, 'texto') ?? texto(conteudo, 'corpo')}
        </p>
      );
  }
}

export function PaginaDoPortal({ slug: slugFixo }: { slug?: string }) {
  const parametros = useParams<{ slug: string }>();
  const slug = slugFixo ?? parametros.slug ?? '';

  const pagina = usePortalPagina(slug);
  const secoes = usePortalSecoes(pagina.data?.id);

  const tituloDaAba = pagina.data?.meta_title ?? pagina.data?.titulo;

  useEffect(() => {
    if (tituloDaAba) {
      document.title = `${tituloDaAba} — Sistema TEG`;
    }
  }, [tituloDaAba]);

  if (pagina.isLoading) {
    return <Spinner size="lg" />;
  }

  if (pagina.isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar esta página agora. Tente de novo em alguns instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void pagina.refetch() }}
        />
      </div>
    );
  }

  if (!pagina.data || !pagina.data.ativo) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <EmptyState
          icon={FileQuestion}
          title="Página não publicada"
          description="Este endereço ainda não tem conteúdo publicado. Volte ao início do portal para encontrar o que procura."
        />
      </div>
    );
  }

  const conteudoDaPagina = pagina.data;
  const secoesAtivas = (secoes.data ?? []).filter((secao) => secao.ativo);
  const consultas = CONSULTAS_POR_PAGINA[slug] ?? [];
  const temProximoPasso = slug === PROXIMO_PASSO_CREDENCIAMENTO.slug;

  return (
    <article>
      <PortalHero
        image={conteudoDaPagina.hero_imagem_url || capaDaPagina(slug)}
        title={conteudoDaPagina.hero_titulo ?? conteudoDaPagina.titulo}
        description={conteudoDaPagina.hero_descricao ?? undefined}
        compact={cabecalhoBaixo(slug)}
      />

      <div className="mx-auto max-w-7xl space-y-10 px-4 py-10 sm:px-6">
        {/*
         * O cartão vem antes de tudo: é a ação da página, e sobe por cima da
         * borda do hero (ver `ProximoPassoCredenciamento`).
         */}
        {temProximoPasso ? <ProximoPassoCredenciamento /> : null}

        {consultas.length > 0 ? <ConsultasDaPagina consultas={consultas} /> : null}

        {secoes.isLoading ? <Spinner /> : null}

        {/*
         * "Conteúdo em preparação" só quando a página não tem nada a oferecer.
         * Consultas fixas e o cartão "Próximo passo" são conteúdo: com eles na
         * tela, dizer que a página está em preparação seria falso — a pessoa
         * já tem o que fazer ali. As seções editoriais aparecem abaixo quando
         * a Secretaria publicar.
         */}
        {!secoes.isLoading &&
        secoesAtivas.length === 0 &&
        consultas.length === 0 &&
        !temProximoPasso ? (
          <EmptyState
            icon={FileQuestion}
            title="Conteúdo em preparação"
            description="Esta página ainda não tem seções publicadas. O conteúdo aparece aqui assim que a Secretaria publicar."
          />
        ) : null}

        {secoesAtivas.map((secao) => (
          <section key={secao.id} aria-labelledby={`secao-${secao.id}`}>
            <h2 id={`secao-${secao.id}`} className="text-xl font-bold">
              {secao.titulo ?? secao.slug_secao}
            </h2>
            {secao.subtitulo ? (
              <p className="mt-1 text-sm text-muted-foreground">{secao.subtitulo}</p>
            ) : null}
            <div className="mt-4">
              <CorpoDaSecao secao={secao} />
            </div>
          </section>
        ))}
      </div>
    </article>
  );
}
